/**
 * A composable function for managing user permissions and accessible applications.
 *
 * This composable provides utilities to check user permissions and determine which
 * applications are available to the current user based on their permissions.
 *
 * @module usePermission
 *
 * @example
 *
 *     const { hasPermission, hasApp, tenantApps } = usePermission();
 *
 *     // Check if user has permission to create users
 *     const canWriteWorkflow = hasPermission('fnd', 'workflows', 'abc-workflow', 'WRITE');
 *
 *     // Check if user has access to a specific app
 *     const canAccessInventory = hasApp('vince-pricer');
 *
 */

import { checkAccess } from '@vincesoftware/vincelive-authorizer';
import { useUserStore } from '../stores/userStore';
import { coreApps, solutionApps } from '../constants/apps';
import { Action } from '../types/roles';
import type { ResourceType } from 'vince-core/types/common';

export enum VinceApp {
    FND = 'fnd',
    CUSTOM_TABLES = 'customtables',
    KANBAN = 'kanban',
    CAMPAIGN = 'campaign',
    IMPEX_HELPER = 'impex-helper',
    MDBREAD_HELPER = 'mdbreadmi-helper',
    MI_TEST = 'mi-test',
    M3_PRINTOUT_CONFIG = 'm3-printout-config',
    VINCE_PRICER = 'vince-pricer',
    CUGEX_HELPER = 'cugex-helper',
    SPYKAR = 'spykar',
    ESHOPBOX = 'eshopbox',
    SCANNER_PICK_PACK = 'scanner-pick-pack',
    PICK_PACK = 'pick-pack',
    CONTRACT_MANAGEMENT = 'contract-management',
    AI_APPS = 'ai-apps',
    SYS_ADMIN = 'sysadmin'
}

export function usePermission () {
    const userStore = useUserStore();

    /**
     * Checks if the current user has a specific permission.
     **/
    const hasPermission = (resource: ResourceType | '*' = '*', resourceId: string = '*', action: Action | '*' = '*', app: VinceApp | string = VinceApp.FND): boolean => {
        try {
            checkAccess({
                // @ts-ignore: need to narrow type later
                auth: {
                    ...userStore.userInfo,
                    ...userStore.userPermission
                },
                truthPermissions: {
                    app,
                    resource,
                    resourceId,
                    action
                }
            });
            return true;
        } catch (e) {
            return false;
        }
    };

    // TODO Extract to auto registering
    const tenantApps = computed(() => {
        const apps = solutionApps.filter(app => hasPermission('*', '*', '*', app.value));
        return [...coreApps, ...apps];
    });

    const hasApp = (appName: string) => tenantApps.value.some(app => app.value === appName);
    return {
        ...userStore,
        currentUser: userStore.userInfo,
        tenantApps,
        hasPermission,
        hasApp
    };
}

/**
 * Helper method to use when only one resource type checking is sufficient
 * @param resourceType type of resource to check permissions on
 */
export function useResourcePermission (resourceType: ResourceType) {
    const permission = usePermission();

    const hasResourcePermission = (action: Action) => {
        return permission.hasPermission(resourceType, action);
    };

    const hasPermission = (resourceId: string, action: Action = Action.READ): boolean => {
        return permission.hasPermission(resourceType, resourceId, action);
    };

    const getPermission = (resourceId: string, action: Action, fallbackAction: Action): Action => {
        return permission.hasPermission(resourceType, resourceId, action) ? action : fallbackAction;
    };

    return {
        hasResourcePermission,
        hasPermission,
        getPermission
    };
}
